import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Datasets } from '../../api/Datasets';
import { VizClient } from '../../api/VizClient';
import AppLoader from '../../components/common/AppLoader';
import { TailSpin } from "react-loader-spinner";
import AppSearch from '../../components/common/AppSearch';
import AssetItem from '../../components/list-items/AssetItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions, getConnectionDetails, createQueryDetails } from '../../utils';
import { Checkbox, Pagination, Select } from "antd";

import windowsLogo from '../../assets/images/windows_logo.png';
import linuxLogo from '../../assets/images/linux_logo.png';
import macLogo from '../../assets/images/mac_logo.png';
import { useMemo } from "react";
import { debounce } from "lodash";
import { useAuth } from "../../contexts/auth";
import { BdaDataset } from '../../models/BdaDataset';
import { AccessLevel, PagedResults } from '../../models/generics';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const statusOptions = [
  { label: "Virtual", value: "false", icon: solid("lock") },
  { label: "Created", value: "true", icon: regular("check-circle") },
];

function DatasetsList() {
  const [datasets, setDatasets] = useState<BdaDataset[]>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [error, setError] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>();
  const [accessLevelFilter, setAccessLevelFilter] = useState<AccessLevel>();
  const [datasourceTypeFilter, setDatasourceTypeFilter] = useState("");
  const [myDatasets, setMyDatasets] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortDesc, setSortDesc] = useState(true);

  const [datasetToDelete, setDatasetToDelete] = useState<BdaDataset>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [show, setShow] = useState(false);

  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [currentPage]);

  // handling current page higher than actual available pages edge case
  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else reset();
    // eslint-disable-next-line
  }, [itemsPerPage, sortDesc, searchText, statusFilter, accessLevelFilter, myDatasets, datasourceTypeFilter]);

  const getDatasets = () => {
    const params = new URLSearchParams();
    params.set("evaluation", "false");
    params.set("page", `${currentPage}`);
    params.set("size", `${itemsPerPage}`);
    params.set("sort", "id");
    params.set("desc", `${sortDesc}`);
    if (myDatasets) params.set("createdBy", `${loggedUser!.userId}`);
    if (statusFilter) params.set("created", statusFilter);
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    // if (datasourceTypeFilter) params.set("datasourceType", datasourceTypeFilter);
    if (searchText.length) params.set("freeText", searchText);

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetPages }) => {
          setDatasets((datasetPages as PagedResults<BdaDataset>).content);
          setTotalDatasets((datasetPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('datasets-error', 'Could not get datasets', 'Datasets Error', 'danger');
          setDatasets([]);
          setError(true);
        }
      );
  }

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setDatasetToDelete(undefined);
  };
  const handleClose = () => {
    setShow(false);
    setDatasetPreview(undefined);
  };
  const handleDownloadClose = () => setShowDownloadModal(false);

  const navigateToCreation = () => navigate('/datasets/create');
  const changeSortMode = () => setSortDesc(!sortDesc);

  const reset = () => {
    setError(false);
    setDatasets(undefined);
    getDatasets();
  }

  const editDataset = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/datasets/${dataset.id}/edit`);
  }

  const onDeleteDataset = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setDatasetToDelete(dataset);
  }

  const deleteDataset = () => {
    showLoader();

    Datasets.deleteDataset(datasetToDelete!.id!)
      .then(
        () => {
          showToast('dataset-delete-success', 'Dataset deleted successfully.', 'Success', 'success');
          hideLoader();
          reset();
          handleDeleteClose();
        },
        () => {
          hideLoader();
          showToast('datasets-error', 'Could not delete dataset', 'Dataset Error', 'danger');
        }
      )
  }

  const handleFilterChange = (filter: 'search' | 'accessLevel' | 'status' | 'datasource') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt.target.value);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;

      case 'status':
        setStatusFilter(evt);
        break;

      case 'datasource':
        setDatasourceTypeFilter(evt);
        break;
    }
  }

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), []);

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }
  const changePage = (page: number) => setCurrentPage(page);
  const datasetDetail = (dataset: BdaDataset) => () => navigate(`/datasets/${dataset.id}`);
  const handleMyDatasetsChange = (evt: CheckboxChangeEvent) => setMyDatasets(evt.target.checked);

  const openDatasetPreview = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShow(true);

    const connectionDetails = getConnectionDetails(dataset.datasource!, undefined, dataset.datasetFileType);
    const body = createQueryDetails(connectionDetails);
    VizClient.getDatasetPreview(body, connectionDetails.type)
      .then(
        ({ data: preview }) => setDatasetPreview(preview?.result?.data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `Dataset Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openDownloadModal = () => setShowDownloadModal(true);

  const downloadClient = (distribution: string) => () => {
    const cdnUrl = `${window.__env.ALIDA_CDN}/releases/ioclient/${distribution}/AlidaIOClient${distribution === "windows" ? '.exe' : ''}`;
    window.open(cdnUrl, "_blank");
  }

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const renderPreview = () => {
    if (!datasetPreview) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={250} />
      </div>
    )

    return datasetPreview.length > 0
      ? (
        <div className="table-wrapper">
          <Table className="mb-0" bordered>
            <thead>
              <tr>
                {Object.keys(datasetPreview[0]).map(key => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datasetPreview.map((row, i) => (
                <tr key={i}>
                  {Object.keys(row).map((col, j) => (
                    <td key={j}>{row[col]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )
      : (
        <Alert className="p-2 mb-0">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Dataset is empty.</span>
        </Alert>
      )
  }

  const renderDatasets = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return datasets!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No datasets found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            datasets!.map(dataset => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={dataset.id}>
                <AssetItem
                  type="dataset"
                  asset={dataset}
                  onEdit={editDataset(dataset)}
                  onDelete={onDeleteDataset(dataset)}
                  onPreview={openDatasetPreview(dataset)}
                  onClick={datasetDetail(dataset)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return datasets
      ? renderDatasets()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      );
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Datasets List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="light" className="me-2 border text-primary" onClick={openDownloadModal}>
              <FontAwesomeIcon icon={solid('file-download')} />
              <span className="d-none d-sm-inline ms-2">Download I/O Client</span>
            </Button>
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Register Dataset</span>
            </Button>
          </Col>
        </Row>

        <Row>
          <Col md={3} className="mb-2">
            <AppSearch placeholder="Search dataset..." onChange={debounceSearchChange} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select status"
              value={statusFilter}
              onChange={handleFilterChange('status')}
              className="w-100"
            >
              {statusOptions.map((opt, index) => (
                <Select.Option key={index} value={opt.value} label={opt.label}>
                  <div>
                    <FontAwesomeIcon icon={opt.icon} fixedWidth />
                    <span className="ms-2">{opt.label}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select privacy level"
              value={accessLevelFilter}
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
          {/* <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select datasource type"
              value={datasourceTypeFilter}
              onChange={handleFilterChange('datasource')}
              className="w-100"
            >
              {datasourceOptions
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col> */}
        </Row>
        <Row>
          <Col md={2}>
            <Checkbox checked={myDatasets} onChange={handleMyDatasetsChange}>My Datasets</Checkbox>
            {/* <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox> */}
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!datasets}
              total={totalDatasets}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Container fluid="md" className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal size="lg" show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Dataset Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderPreview()}</Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDownloadModal} onHide={handleDownloadClose}>
        <Modal.Header closeButton>
          <Modal.Title>Download I/O Client</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p>Choose your distribution below:</p>

          <Row className="justify-content-center mb-3">
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('windows')}>
                <img src={windowsLogo} height={75} alt="Windows distribution" />
                <h6 className="mt-3 mb-0">Windows</h6>
              </Button>
            </Col>
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('mac')}>
                <img src={macLogo} height={75} alt="MacOS distribution" />
                <h6 className="mt-3 mb-0">Mac OS</h6>
              </Button>
            </Col>
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('linux')}>
                <img src={linuxLogo} height={75} alt="Linux distribution" />
                <h6 className="mt-3 mb-0">Linux</h6>
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Dataset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete dataset <b>{datasetToDelete && datasetToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteDataset}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(DatasetsList);
